import React, { useEffect, useRef, useState } from 'react';
import * as turf from '@turf/turf';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { ArrowForward, Cancel } from '@mui/icons-material';
import { generatePath, useHistory } from 'react-router-dom';
import { Colors } from '../../../assets/theme/Colors';
import routes from '../../../routes';
import { MARKER_DONOR, MARKER_RECEIVER, MARKER_RESCUER, MARKER_UNASSIGNED_USER } from './MarkersSelector';

const svgString = `
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="${Colors.blue.main}">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
  </svg>`;
const cursorUrl = `url('data:image/svg+xml,${encodeURIComponent(svgString)}') 3 21, auto`;

const LassoOverlay = ({ mapApi, onSelection, onCancel, removeSelectedMarkers, users = [], show = false }) => {
  const history = useHistory();
  const containerRef = useRef(null);
  const drawing = useRef(false);
  const pointsRef = useRef([]);
  const overlayRef = useRef(null);
  const usersRef = useRef(users);
  const markersRef = useRef([]);
  const [points, setPoints] = useState([]);
  const [selectionInfo, setSelectionInfo] = useState(null);

  const { map, maps } = mapApi || {};

  useEffect(() => {
    usersRef.current = users;
  }, [users]);

  useEffect(() => {
    if (!map || !maps) {
      return undefined;
    }

    const overlay = new maps.OverlayView();
    overlay.onAdd = () => {};
    overlay.draw = () => {};
    overlay.onRemove = () => {};
    overlay.setMap(map);
    overlayRef.current = overlay;

    return () => {
      overlay.setMap(null);
      overlayRef.current = null;
    };
  }, [map, maps]);

  useEffect(() => {
    if (!map || !maps || !show) {
      pointsRef.current = [];
      setPoints([]);
      setSelectionInfo(null);
      return undefined;
    }

    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    const clearPolygon = () => {
      if (drawing.current) {
        return;
      }

      pointsRef.current = [];
      setPoints([]);
      setSelectionInfo(null);
    };

    const getMousePosition = (event) => {
      const rect = container.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    };

    const mouseDown = (event) => {
      drawing.current = true;
      removeSelectedMarkers(markersRef.current);
      const pos = getMousePosition(event);
      pointsRef.current = [[pos.x, pos.y]];
      setPoints(pointsRef.current);
      map.setOptions({ draggable: false });
      setSelectionInfo(null);
    };

    const mouseMove = (event) => {
      if (!drawing.current) {
        return;
      }
      const pos = getMousePosition(event);
      pointsRef.current = [...pointsRef.current, [pos.x, pos.y]];
      setPoints(pointsRef.current);
    };

    const mouseUp = () => {
      if (!drawing.current) {
        return;
      }

      drawing.current = false;
      map.setOptions({ draggable: true });

      const currentPoints = pointsRef.current;

      if (currentPoints.length < 3 || !overlayRef.current) {
        return;
      }

      const projection = overlayRef.current.getProjection();

      if (!projection) {
        pointsRef.current = [];
        setPoints([]);
        return;
      }

      const mapRect = map.getDiv().getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const offsetX = containerRect.left - mapRect.left;
      const offsetY = containerRect.top - mapRect.top;

      const polygonCoordinates = currentPoints.map(([x, y]) => {
        const latLng = projection.fromContainerPixelToLatLng(new maps.Point(x + offsetX, y + offsetY));
        return [latLng.lng(), latLng.lat()];
      });

      polygonCoordinates.push(polygonCoordinates[0]);

      const polygon = turf.polygon([polygonCoordinates]);

      const selected = usersRef.current.filter((user) =>
        turf.booleanPointInPolygon(turf.point([user.meta.long, user.meta.lat]), polygon)
      );

      // Get anchor points of polygon for selection inof
      const xs = currentPoints.map(([x]) => x);
      const ys = currentPoints.map(([, y]) => y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);

      const selectedUsers = selected.filter((marker) => marker.meta._markerType !== MARKER_UNASSIGNED_USER);
      const unaasignedUsers = selected.filter((marker) => marker.meta._markerType === MARKER_UNASSIGNED_USER);

      setSelectionInfo({
        anchorX: (minX + maxX) / 2,
        anchorY: minY,
        count: selectedUsers.length,
        markers: selectedUsers,
        unassignedCount: unaasignedUsers.length,
        unaasignedMarkers: unaasignedUsers,
      });
      markersRef.current = selected;

      onSelection(selected);
    };

    container.addEventListener('mousedown', mouseDown);
    window.addEventListener('mousemove', mouseMove);
    window.addEventListener('mouseup', mouseUp);

    const dragListener = maps.event.addListener(map, 'dragstart', clearPolygon);
    const zoomListener = maps.event.addListener(map, 'zoom_changed', clearPolygon);

    return () => {
      container.removeEventListener('mousedown', mouseDown);
      window.removeEventListener('mousemove', mouseMove);
      window.removeEventListener('mouseup', mouseUp);
      maps.event.removeListener(dragListener);
      maps.event.removeListener(zoomListener);
    };
  }, [map, maps, show]);

  const handleGenerateList = () => {
    if (selectionInfo?.markers?.length) {
      const formattedUsers = selectionInfo?.markers.map((marker) => {
        const type = marker.meta._markerType;
        switch (type) {
          case MARKER_DONOR:
          case MARKER_RECEIVER:
            return marker.meta.primary_contact;
          case MARKER_RESCUER:
            return { ...marker.meta, id: marker.meta?.user_id };
          default:
            return marker.meta;
        }
      });
      const usersMissingPrimaryContact = selectionInfo?.markers.filter(
          (marker) =>
            (marker.meta._markerType === MARKER_DONOR || marker.meta._markerType === MARKER_RECEIVER) &&
            !marker.meta.primary_contact
        )
        .map((marker) => marker.meta);

      history.push(generatePath(routes.allUsers, { tab: 'users' }), {
        selectedUsers: formattedUsers,
        usersWithoutPrimaryContact: usersMissingPrimaryContact,
      });
    }
  };

  const handleGenerateUnassignedList = () => {
    if (selectionInfo?.unaasignedMarkers?.length) {
      const formattedUsers = selectionInfo?.unaasignedMarkers.map((marker) => marker.meta);

      history.push(generatePath(routes.unassignedUsers), { selectedUsers: formattedUsers });
    }
  };

  if (!mapApi || !show) {
    return null;
  }

  return (
    <>
      <svg
        ref={containerRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 4,
          cursor: cursorUrl,
          pointerEvents: 'all',
        }}
      >
        <polygon
          points={points.map((p) => p.join(',')).join(' ')}
          fill="rgba(0,120,255,0.15)"
          stroke={Colors.blue.main}
          strokeWidth="2"
        />
      </svg>
      {!!selectionInfo && (
        <Box
          sx={{
            position: 'absolute',
            left: selectionInfo.anchorX,
            top: selectionInfo.anchorY,
            transform: 'translate(-50%, calc(-100% - 8px))',
            zIndex: 5,
          }}
        >
          {!!selectionInfo?.unaasignedMarkers?.length && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 5,
                  px: 1,
                  py: 0.5,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: 10,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {selectionInfo.unassignedCount} unassigned selected
                </Typography>
                <IconButton size="small" onClick={onCancel}>
                  <Cancel sx={{ color: 'text.disabled', fontSize: 16 }} />
                </IconButton>
              </Box>
              <Button
                color="primary"
                variant="contained"
                endIcon={<ArrowForward fontSize="small" />}
                sx={{ textTransform: 'none', boxShadow: 10 }}
                onClick={handleGenerateUnassignedList}
              >
                Generate Unassigned List
              </Button>
            </Box>
          )}

          {!!selectionInfo?.markers.length && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 5,
                  px: 1,
                  py: 0.5,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: 10,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {selectionInfo.count} users selected
                </Typography>
                <IconButton size="small" onClick={onCancel}>
                  <Cancel sx={{ color: 'text.disabled', fontSize: 16 }} />
                </IconButton>
              </Box>
              <Button
                color="secondary"
                variant="contained"
                endIcon={<ArrowForward fontSize="small" />}
                sx={{ textTransform: 'none', boxShadow: 10 }}
                onClick={handleGenerateList}
              >
                Generate List
              </Button>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default LassoOverlay;
